import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import { DASHBOARD_LIST_PAGE_SIZE } from "../constants/dashboardListPagination";
import {
  IOwnerVisitsData,
  OwnerVisitsRequestStatus,
} from "../types/ownerDashboard.types";
import { OwnerVisitsQuery, buildOwnerVisitsParams } from "../utils/buildOwnerVisitsParams";
import { normalizeOwnerVisitsData } from "../utils/normalizeOwnerVisitsData";

interface UseOwnerVisitsOptions {
  accessToken: string | null;
  enabled: boolean;
  query: Omit<OwnerVisitsQuery, "pastPage" | "pastPerPage" | "upcomingPage" | "upcomingPerPage">;
}

interface UseOwnerVisitsResult {
  visitsData: IOwnerVisitsData | null;
  status: OwnerVisitsRequestStatus;
  pastPage: number;
  upcomingPage: number;
  setPastPage: (page: number) => void;
  setUpcomingPage: (page: number) => void;
  refetch: () => Promise<void>;
}

export function useOwnerVisits({
  accessToken,
  enabled,
  query,
}: UseOwnerVisitsOptions): UseOwnerVisitsResult {
  const [visitsData, setVisitsData] = useState<IOwnerVisitsData | null>(null);
  const [status, setStatus] = useState<OwnerVisitsRequestStatus>("idle");
  const [pastPage, setPastPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    setPastPage(1);
    setUpcomingPage(1);
  }, [query.periodPreset, query.propertyId, query.customPeriod?.initial_date, query.customPeriod?.final_date]);

  const fetchVisits = useCallback(async () => {
    if (!enabled || !accessToken) {
      return;
    }

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    setStatus("loading");

    try {
      api.defaults.headers.authorization = `Bearer ${accessToken}`;

      const response = await api.get("/api/client/owner/visits", {
        params: buildOwnerVisitsParams({
          ...query,
          pastPage,
          pastPerPage: DASHBOARD_LIST_PAGE_SIZE,
          upcomingPage,
          upcomingPerPage: DASHBOARD_LIST_PAGE_SIZE,
        }),
      });

      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      const normalizedData = normalizeOwnerVisitsData(response.data?.data);

      if (!normalizedData) {
        setVisitsData(null);
        setStatus("error");
        return;
      }

      setVisitsData(normalizedData);
      setStatus("success");
    } catch {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setVisitsData(null);
      setStatus("error");
    }
  }, [accessToken, enabled, pastPage, query, upcomingPage]);

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return;
    }

    fetchVisits();
  }, [enabled, fetchVisits]);

  return {
    visitsData,
    status,
    pastPage,
    upcomingPage,
    setPastPage,
    setUpcomingPage,
    refetch: fetchVisits,
  };
}

export type { OwnerVisitsQuery };
